import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  CreateOfficialExpenseClient,
  DeleteOfficialExpenseClient,
  UpdateOfficialExpenseClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const createOfficialExpenseClient = new CreateOfficialExpenseClient(SERVICE_URL)
const deleteOfficialExpenseClient = new DeleteOfficialExpenseClient(SERVICE_URL)
const updateOfficialExpenseClient = new UpdateOfficialExpenseClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  isRedirectPage: false,
  error: null,
  isDeleted: false,
  officialExpense: null,
  detail: null,
}

// Async Thunks
export const officialExpenseCreate = createAsyncThunk(
  'official/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await createOfficialExpenseClient.create(
        params?.ExpenseInvoiceId,
        params?.Name,
        params?.InvoiceNumber,
        params?.TotalPrice,
        params?.TotalTax,
        params?.TotalWithHolding,
        params?.OfficialExpenseFile,
        params?.InvoiceDate
      )
      dispatch(setOfficialExpense(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const officialExpenseDelete = createAsyncThunk(
  'official/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await deleteOfficialExpenseClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const officialExpenseUpdate = createAsyncThunk(
  'official/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await updateOfficialExpenseClient.update(params)
      dispatch(setOfficialExpense(response || params))
      dispatch(setRedirectPage(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const officialSlice = createSlice({
  name: 'official',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setOfficialExpense: (state, action) => {
      state.officialExpense = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('official/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setOfficialExpense,
  setDeleted,
  setRedirectPage,
  setDetail,
  resetState,
} = officialSlice.actions

// Selectors
export const selectOfficialLoading = (state) => state.official.loading
export const selectOfficialError = (state) => state.official.error
export const selectOfficialExpense = (state) => state.official.officialExpense
export const selectOfficialDeleted = (state) => state.official.isDeleted
export const selectOfficialRedirectPage = (state) => state.official.isRedirectPage
export const selectOfficialDetail = (state) => state.official.detail

export default officialSlice.reducer
